import { Pressable, StyleSheet } from "react-native";
import { Icon, IconButton } from "react-native-paper";
import { useAppTheme } from "../../useAppTheme";
import Box from "../Box";

type PlaybackControlsProps = {
	isPlaying: boolean;
	isFirstTrack: boolean;
	isLastTrack: boolean;
	compact?: boolean;
	onPlayPause: () => void;
	onSkipPrevious: () => void;
	onSkipNext: () => void;
	onRewind: () => void;
	onForward: () => void;
};

const PlaybackControls = ({
	isPlaying,
	isFirstTrack,
	isLastTrack,
	compact,
	onPlayPause,
	onSkipPrevious,
	onSkipNext,
	onRewind,
	onForward,
}: PlaybackControlsProps) => {
	const theme = useAppTheme();

	const iconSize = compact ? 18 : 24;
	const playIconSize = compact ? 18 : 24;
	const playCircleSize = compact ? 36 : 40;
	const buttonStyle = compact
		? styles.controlButtonCompact
		: styles.controlButton;
	const playOuterStyle = compact
		? styles.playButtonOuterCompact
		: styles.playButtonOuter;

	return (
		<Box
			flexDirection="row"
			alignItems="center"
			justifyContent={compact ? "center" : "space-between"}
			gap={compact ? 20 : undefined}
			padding={compact ? 0 : 8}
			borderRadius={compact ? undefined : 100}
			backgroundColor={compact ? undefined : theme.colors.surfaceVariant}
			borderWidth={compact ? 0 : 1}
			borderColor={compact ? undefined : theme.colors.outlineVariant}
			width={compact ? undefined : "100%"}
		>
			<IconButton
				icon="skip-previous"
				size={iconSize}
				onPress={onSkipPrevious}
				disabled={isFirstTrack}
				iconColor={theme.colors.onSurfaceVariant}
				style={buttonStyle}
				accessibilityLabel="Skip to previous track"
			/>
			<IconButton
				icon="rewind-10"
				size={iconSize}
				onPress={onRewind}
				iconColor={theme.colors.onSurfaceVariant}
				style={buttonStyle}
				accessibilityLabel="Rewind 10 seconds"
			/>
			<Pressable
				onPress={onPlayPause}
				style={playOuterStyle}
				accessibilityRole="button"
				accessibilityLabel={isPlaying ? "Pause" : "Play"}
			>
				<Box
					width={playCircleSize}
					height={playCircleSize}
					borderRadius={9999}
					backgroundColor={theme.colors.primary}
					alignItems="center"
					justifyContent="center"
				>
					<Icon
						source={isPlaying ? "pause" : "play"}
						size={playIconSize}
						color={theme.colors.onPrimary}
					/>
				</Box>
			</Pressable>
			<IconButton
				icon="fast-forward-30"
				size={iconSize}
				onPress={onForward}
				iconColor={theme.colors.onSurfaceVariant}
				style={buttonStyle}
				accessibilityLabel="Fast forward 30 seconds"
			/>
			<IconButton
				icon="skip-next"
				size={iconSize}
				onPress={onSkipNext}
				disabled={isLastTrack}
				iconColor={theme.colors.onSurfaceVariant}
				style={buttonStyle}
				accessibilityLabel="Skip to next track"
			/>
		</Box>
	);
};

const styles = StyleSheet.create({
	playButtonOuter: {
		width: 48,
		height: 48,
		alignItems: "center",
		justifyContent: "center",
	},
	playButtonOuterCompact: {
		width: 36,
		height: 36,
		alignItems: "center",
		justifyContent: "center",
	},
	controlButton: {
		margin: 0,
		width: 48,
		height: 48,
	},
	controlButtonCompact: {
		margin: 0,
		width: 36,
		height: 36,
	},
});

export default PlaybackControls;
